// ══════════════════════════════════════════════════════════════════
//  ED CRM - Wrapper de Toast (sobre sonner)
// ══════════════════════════════════════════════════════════════════
//  Centraliza o uso da biblioteca de toast para que componentes não
//  importem `sonner` diretamente. Se a lib mudar no futuro, só este
//  arquivo precisa ser ajustado.
//
//  Uso:
//    import { toast } from '@utils';
//    toast.success('Presença confirmada');
//    toast.error('Falha ao salvar', err.message);
//    const id = toast.loading('Enviando…');
//    toast.dismiss(id);
//
//  O <Toaster /> precisa estar montado em algum lugar acima na árvore
//  (já é, em App.js). Sem isso, as chamadas viram no-ops silenciosos.
// ══════════════════════════════════════════════════════════════════

import { toast as sonner } from 'sonner';

export type ToastId = string | number;

const DEFAULT_DURATION = 4000;

export const toast = {
  success(message: string, description?: string): ToastId {
    return sonner.success(message, { description, duration: DEFAULT_DURATION });
  },
  error(message: string, description?: string): ToastId {
    return sonner.error(message, { description, duration: DEFAULT_DURATION + 2000 });
  },
  info(message: string, description?: string): ToastId {
    return sonner.info?.(message, { description, duration: DEFAULT_DURATION })
      ?? sonner(message, { description, duration: DEFAULT_DURATION });
  },
  loading(message: string): ToastId {
    return sonner.loading(message);
  },
  dismiss(id?: ToastId): ToastId {
    return id != null ? sonner.dismiss(id) : sonner.dismiss();
  },
};

// Helper para erros vindos do Supabase: aceita Error, string ou
// { message, error_description }.
export const toastError = (titulo: string, err?: unknown): ToastId => {
  let desc = '';
  if (typeof err === 'string') {
    desc = err;
  } else if (err && typeof err === 'object') {
    const e = err as { message?: string; error_description?: string; details?: string };
    desc = e.message || e.error_description || e.details || '';
  }
  return toast.error(titulo, desc);
};

export default toast;
